import { useState, type InputHTMLAttributes, type ReactNode } from 'react'
import type { CustomerAuthField } from '../../../hooks/customer-auth-form/validation'
import type { CustomerAuthFieldUiHelpers } from './types'
import { motion } from 'motion/react'

type CustomerAuthInputFieldProps = {
  field: CustomerAuthField
  label: string
  value: string
  onChange: (value: string) => void
  ui: CustomerAuthFieldUiHelpers
  extraClassName?: string
  showRequiredMark?: boolean
  normalizeValue?: (value: string) => string
  allowPasswordToggle?: boolean
  showPasswordLabel?: string
  hidePasswordLabel?: string
  labelAction?: ReactNode
} & Pick<
  InputHTMLAttributes<HTMLInputElement>,
  | 'type'
  | 'placeholder'
  | 'autoComplete'
  | 'autoCorrect'
  | 'spellCheck'
  | 'required'
  | 'minLength'
  | 'maxLength'
  | 'autoCapitalize'
  | 'inputMode'
  | 'pattern'
  | 'step'
  | 'min'
  | 'max'
  | 'disabled'
>

export const CustomerAuthInputField = ({
  field,
  label,
  value,
  onChange,
  ui,
  extraClassName = '',
  showRequiredMark = true,
  normalizeValue,
  allowPasswordToggle = false,
  showPasswordLabel = 'Mostra password',
  hidePasswordLabel = 'Nascondi password',
  labelAction,
  type = 'text',
  placeholder,
  autoComplete,
  autoCorrect,
  spellCheck,
  required,
  minLength,
  maxLength,
  autoCapitalize,
  inputMode,
  pattern,
  step,
  min,
  max,
  disabled,
}: CustomerAuthInputFieldProps) => {
  const [passwordVisible, setPasswordVisible] = useState(false)
  const [focusWithin, setFocusWithin] = useState(false)
  const canTogglePassword = allowPasswordToggle && type === 'password'
  const effectiveInputType = canTogglePassword ? (passwordVisible ? 'text' : 'password') : type

  return (
    <motion.div
      animate={{ scale: focusWithin ? 1.01 : 1 }}
      transition={{ duration: 0.15 }}
      onFocusCapture={() => setFocusWithin(true)}
      onBlurCapture={(event) => {
        const next = event.relatedTarget as Node | null
        if (!event.currentTarget.contains(next)) setFocusWithin(false)
      }}
    >
      <label className="grid min-w-0 gap-1 text-sm font-medium text-(--color-text-secondary)">
        <span className="flex items-center justify-between gap-2">
          <span>
            {label}
            {showRequiredMark ? ui.fieldLabelRequiredMark : null}
          </span>
          {labelAction ? <span className="shrink-0">{labelAction}</span> : null}
        </span>
        <div className="relative">
          <input
            type={effectiveInputType}
            value={value}
            onChange={(event) => {
              const nextValue = normalizeValue ? normalizeValue(event.target.value) : event.target.value
              onChange(nextValue)
            }}
            onBlur={() => ui.onFieldBlur(field)}
            placeholder={placeholder}
            autoComplete={autoComplete}
            autoCorrect={autoCorrect}
            spellCheck={spellCheck}
            required={required}
            minLength={minLength}
            maxLength={maxLength}
            autoCapitalize={autoCapitalize}
            inputMode={inputMode}
            pattern={pattern}
            step={step}
            min={min}
            max={max}
            disabled={disabled}
            aria-invalid={Boolean(ui.resolveFieldError(field))}
            aria-describedby={ui.resolveErrorDescribedBy(field)}
            className={ui.resolveInputClassName(
              field,
              `${canTogglePassword ? 'pr-11' : ''} ${extraClassName}`.trim(),
            )}
          />
          {canTogglePassword ? (
            <button
              type="button"
              onClick={() => setPasswordVisible((current) => !current)}
              className="absolute inset-y-0 right-0 inline-flex h-full w-11 cursor-pointer items-center justify-center text-(--color-text-muted) transition-colors duration-150 hover:text-(--color-text-primary) focus-visible:outline-none focus-visible:text-(--color-focus-ring)"
              aria-label={passwordVisible ? hidePasswordLabel : showPasswordLabel}
              aria-pressed={passwordVisible}
              title={passwordVisible ? hidePasswordLabel : showPasswordLabel}
            >
              {passwordVisible ? (
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-[1.9]" aria-hidden>
                  <path d="M3 3 21 21" />
                  <path d="M10.6 10.6A2 2 0 0 0 13.4 13.4" />
                  <path d="M9.9 5.2A10.2 10.2 0 0 1 12 5c5.7 0 9.5 4.7 10.5 7-1 1.9-3.7 5.4-7.8 6.6" />
                  <path d="M6.1 6.1C3.8 7.5 2.4 9.7 1.5 12c1 2.3 4.8 7 10.5 7 1 0 2-.1 2.9-.4" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current stroke-[1.9]" aria-hidden>
                  <path d="M1.5 12c1-2.3 4.8-7 10.5-7s9.5 4.7 10.5 7c-1 2.3-4.8 7-10.5 7S2.5 14.3 1.5 12Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          ) : null}
        </div>
        {ui.renderFieldHint(field)}
      </label>
    </motion.div>
  )
}
